import { useState } from 'react'

import type { IPortfolioInvestmentItem } from '~/@api/generated-api-types-account/Api'
import {
  addDays,
  formatDate,
  formatDateTimeWithSeconds,
} from '~/@lib/utils/date.utils'
import { _killPropagation } from '~/@lib/utils/ui.utils'
import { formatAmount } from '~/@lib/utils/number.utils'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { useFinancialOverviewQuery } from '~/@pages_account-portfolio/financial-overview/useFinancialOverviewQuery'
import { useUser } from '~/@biz_account/useUser/useUser'
import { useInvestmentsQuery } from '../../investments-table/useInvestmentsQuery'
import { useInvestmentReservationWithdrawableSetting } from './useInvestmentReservationWithdrawableSetting'
import { WithdrewInvestmentDialog } from './WithdrawInvestmentDialog'
import { TextTooltip } from '~/@mid_ui/text-tooltip/TextTooltip'
import { useQueryClient } from '~/@mid_query/client/useQueryClient'

type Props = { investment: IPortfolioInvestmentItem }

export function WithdrawInvestmentTooltip({ investment }: Props) {
  const [isWidthdrewDialogOpen, setWidthdrewDialogOpen] = useState(false)
  const { invalidateQueries } = useQueryClient()
  const { reloadInvestments } = useInvestmentsQuery({
    filter: undefined,
    sort: undefined,
  })
  const { setNotification } = useLocalNotifications()
  const { investmentReservationWithdrawableSetting } =
    useInvestmentReservationWithdrawableSetting()
  const { reloadAggregates } = useFinancialOverviewQuery()
  const { reloadUser } = useUser()

  function closeDialog() {
    setWidthdrewDialogOpen(false)
  }

  async function handleWithdrawedInvestmentReservation() {
    const investedAmount = formatAmount(investment.value)
    await reloadInvestments()
    await reloadAggregates()
    await invalidateQueries({ queryKey: ['transactions'] })
    await reloadUser()
    setNotification(
      'success',
      `Investíciu v hodnote ${investedAmount} sme zrušili`
    )
    closeDialog()
  }

  function handleClickButton(e: React.MouseEvent) {
    _killPropagation(e)
    setWidthdrewDialogOpen(true)
  }

  const withdrawDate = addDays(
    investment.createdAt,
    investmentReservationWithdrawableSetting?.withdrawWindowDurationDays ?? 0
  )

  const withdrawDateFormatted = formatDate(withdrawDate.toISOString())
  const withdrawDateTimeFormatted = formatDateTimeWithSeconds(withdrawDate)

  if (
    !investmentReservationWithdrawableSetting?.enabled ||
    !investment.isWithdrawable
  ) {
    return null
  }

  return (
    <>
      <TextTooltip
        clickable
        color="#F0A000"
        text={`K tejto investícii práve beží 4 denná ochranná lehota - Investíciu
          môžete zrušiť do ${withdrawDateFormatted}`}
        buttonText="Využiť ochrannú lehotu"
        buttonAction={handleClickButton}
      />
      {isWidthdrewDialogOpen && (
        <WithdrewInvestmentDialog
          investment={investment}
          withdrawDateTimeFormatted={withdrawDateTimeFormatted}
          withdrawWindowDurationDays={
            investmentReservationWithdrawableSetting?.withdrawWindowDurationDays ??
            0
          }
          onClose={closeDialog}
          onWithdrawedInvestmentReservation={
            handleWithdrawedInvestmentReservation
          }
        />
      )}
    </>
  )
}
